import { useT } from "@transifex/react";
import { AxiosResponse } from "axios";
import { BookContextProps } from "p6m-contexts";
import {
    AvailableBandsInfo,
    GetBandInfoResponse,
    RecentBook,
    RecentBooks,
    RecentBookSubjects,
    SelectedBookData,
} from "p6m-p6u";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { CreationModes } from "../constants/CreationFlowConstants";
import { SourceType } from "../enums/sources";
import { isBookBlockedByFilters, type ActivityBook, type BookFilters } from "../helpers/BookAvailability";
import { getRequest } from "../helpers/networkHelper";
import { useGeneralContext } from "./AppGeneralContext";
import { useTestCreationContext } from "./TestCreationContext";

const BookContext = React.createContext<BookContextProps | undefined>(undefined);

// The LRS diktat book, by its p6u id and by its book id in the shop search the catalog comes from.
const BLOCKED_BOOK_IDS = ["phase6_lrs", "456"];
// Module-level, so each mode's filters keep their identity across renders for the hooks memoizing on them.
const TEST_BOOK_FILTERS: BookFilters = {
    blockedSubjects: ["sonstige", "german", "ancientgreek", "arabic", "chinese"],
    blockedPublishers: ["lawbility", "hefei_huang_verlag"],
    blockedBooks: BLOCKED_BOOK_IDS,
};
const RT_BOOK_FILTERS: BookFilters = {
    blockedSubjects: ["sonstige", "german"],
    blockedPublishers: [],
    blockedBooks: BLOCKED_BOOK_IDS,
};

/** Undefined instead of throwing: the lists resolving books in bulk drop the ones they can't get. */
async function _fetchBandInfo(book: ActivityBook): Promise<GetBandInfoResponse | undefined> {
    try {
        const bandInfoResponse = await getRequest(
            "p6u/get-band-info/",
            book.isUuid ? { bandId: book.id, isUuid: true } : { bandId: book.id }
        );
        return bandInfoResponse?.data as GetBandInfoResponse | undefined;
    } catch {
        return undefined;
    }
}

/**
 * recent-articles carries no subject, so honouring the blocked-subject list means resolving each
 * book's band info — one request per book, hence the caller's loading flag. Books whose lookup
 * fails are left out of the map rather than failing the load: an outage should not empty the
 * teacher's recents.
 */
async function _resolveBookSubjects(books: Array<RecentBook>): Promise<RecentBookSubjects> {
    const entries = await Promise.all(
        books.map(async (book) => {
            if (!book.articleId) return null;
            const subjectId = (await _fetchBandInfo({ id: book.articleId, isUuid: false }))?.subject?.ID;
            return subjectId ? ([book.articleId, subjectId] as const) : null;
        })
    );
    return Object.fromEntries(entries.filter(Boolean) as Array<readonly [string, string]>);
}

function useBookContext() {
    const context = React.useContext(BookContext);

    if (!context) {
        throw new Error("useBookContext must be used inside the provider");
    }

    const { setIsDataBeingLoaded, creatingMode, setCreatingMode } = useGeneralContext();

    const {
        selectedBookData,
        setSelectedBookData,
        removeContentFromVocabularyDrawer,
        userOwnSubjects,
        setTestName,
        loadInitArticleIdData,
        openWarningModal,
    } = useTestCreationContext();

    const history = useHistory();

    const t = useT();
    const t_invalidArticleId = t("It was not possible to load the requested book.", {
        _tags: "CreateTest,SelectExerciseType",
    });
    const t_errorLoadingBookInfo = t("There was an error while loading book information.", {
        _tags: "CreateTest,SelectExerciseType",
    });
    const t_bookNotAvailableForTest = t("This book is not available for tests.", {
        _tags: "CreateTest,RecentlyUsedContent",
    });

    const bookFilters = creatingMode === CreationModes.RECURRING_TASK ? RT_BOOK_FILTERS : TEST_BOOK_FILTERS;
    // "sonstige" is the own-content pseudo-subject, never a book's subject, so it can't filter books.
    const blockedBookSubjects = bookFilters.blockedSubjects.filter((subjectId) => subjectId !== "sonstige");

    const isBookBlocked = (bandInfo?: SelectedBookData) =>
        isBookBlockedByFilters(bandInfo, {
            blockedSubjects: blockedBookSubjects,
            blockedPublishers: bookFilters.blockedPublishers,
            blockedBooks: bookFilters.blockedBooks,
        });

    const {
        recentlyUsedBooks,
        setRecentlyUsedBooks,
        setRecentBookSubjects,
        setAreRecentBooksLoading,
        userUsedBooksInfo,
        setUserUsedBooksInfo,
    } = context;

    async function loadBookFromRecentBooks(articleId: string) {
        if (
            selectedBookData.band &&
            selectedBookData.band.ID &&
            parseInt(selectedBookData.band.ID) === parseInt(articleId)
        ) {
            // Already-selected books skip the band-info fetch, so check the cached subject here too.
            if (selectedBookData.subject?.ID && blockedBookSubjects.includes(selectedBookData.subject.ID)) {
                openWarningModal(t_bookNotAvailableForTest);
                return;
            }
            history.push("/create/select-vocabulary");
            return;
        }

        setIsDataBeingLoaded(true);
        const book = recentlyUsedBooks[articleId];

        if (book) {
            setIsDataBeingLoaded(true);
            try {
                const bandInfoResponse = await getRequest("p6u/get-band-info/", { bandId: articleId });
                if (!bandInfoResponse || !bandInfoResponse.data) {
                    throw new Error("No data was loaded");
                }
                const bandData: GetBandInfoResponse = bandInfoResponse.data;
                // Backstop for books the recents filter had to keep because their lookup failed.
                if (bandData.subject?.ID && blockedBookSubjects.includes(bandData.subject.ID)) {
                    openWarningModal(t_bookNotAvailableForTest);
                    return;
                }
                setSelectedBookData({ ...bandData });

                history.push("/create/select-vocabulary");
                removeContentFromVocabularyDrawer("BOOK");
            } catch (e) {
            } finally {
                setIsDataBeingLoaded(false);
            }
        } else {
            alert("cant");
            setIsDataBeingLoaded(false);
        }
    }

    async function loadUserRecentBooks() {
        let recentBooks: RecentBooks = {};
        let recentBooksArray: Array<RecentBook> = [];

        setAreRecentBooksLoading(true);
        try {
            const recentBooksResponse: AxiosResponse<Array<RecentBook>> = await getRequest("aea/recent-articles/");

            recentBooksArray = recentBooksResponse.data;
            recentBooksArray.sort((a, b) => (b.date || 0) - (a.date || 0));
            recentBooksArray = recentBooksArray.filter((t) => !!t.name);
            recentBooksArray = recentBooksArray.filter(
                (t) => !t.ownSubjectId || (t.ownSubjectId && userOwnSubjects.some((s) => s.id === t.ownSubjectId))
            );
            recentBooksArray.forEach((book) => {
                if (!book.articleId && !book.ownSubjectId) {
                    return;
                }
                recentBooks[(book.articleId || book.ownSubjectId)!] = {
                    ...book,
                };
            });

            // Storing the list unfiltered keeps both source screens writing identical state, so a
            // slow call from one can't clobber the other's view.
            const bookSubjects = await _resolveBookSubjects(recentBooksArray);
            setRecentlyUsedBooks({ ...recentBooks });
            setRecentBookSubjects(bookSubjects);
        } catch (e: any) {
            // Leaves any previously loaded books in place: a failed refetch shouldn't empty the list.
        } finally {
            setAreRecentBooksLoading(false);
        }
    }

    async function loadBooksInfoFromUUIDsIntoState(bookUuids: Array<string>) {
        // Only the newly fetched ones, merged functionally: this runs alongside
        // loadBandInfosForActivitiesIntoState, and a snapshot would drop whatever it resolved meanwhile.
        const loadedBooksInfo: AvailableBandsInfo = {};

        for (let i = 0; i < bookUuids.length; i++) {
            const bookUuid = bookUuids[i];
            if (userUsedBooksInfo[bookUuid]?.band?.ID) {
                continue;
            }
            try {
                const bandInfoResponse = await getRequest("p6u/get-band-info/", { bandId: bookUuid, isUuid: true });
                if (!bandInfoResponse || !bandInfoResponse.data) {
                    throw new Error("No data was loaded");
                }
                loadedBooksInfo[bookUuid] = bandInfoResponse.data;
            } catch {
                openWarningModal(t_errorLoadingBookInfo);
            }
        }
        setUserUsedBooksInfo((previousInfos) => ({ ...previousInfos, ...loadedBooksInfo }));
    }

    /**
     * Band infos for the activity lists. Parallel and silent where loadBooksInfoFromUUIDsIntoState
     * is sequential and warns: a book that fails here only stays unfiltered — the check on
     * selection still catches it — and one modal per activity would be noise.
     */
    async function loadBandInfosForActivitiesIntoState(books: Array<ActivityBook>) {
        const missingBooks = books.filter((book) => !userUsedBooksInfo[book.id]);
        if (missingBooks.length === 0) {
            return;
        }

        const entries = await Promise.all(
            missingBooks.map(async (book) => {
                const bandInfo = await _fetchBandInfo(book);
                return bandInfo ? ([book.id, bandInfo] as const) : null;
            })
        );

        const resolvedInfos = Object.fromEntries(
            entries.filter(Boolean) as Array<readonly [string, GetBandInfoResponse]>
        );
        // Functional update: the trainings' own resolving may write to the same map meanwhile.
        setUserUsedBooksInfo((previousInfos) => ({ ...previousInfos, ...resolvedInfos }));
    }

    async function checkForInitArticleId(defaultTestName: string) {
        const initArticleId = localStorage.getItem("initAID");

        if (initArticleId) {
            try {
                setTestName(defaultTestName);
                await loadInitArticleIdData(initArticleId);
                localStorage.removeItem("initAID");
                setCreatingMode(CreationModes.TEST);
                setTimeout(() => {
                    history.push("/create/select-book");
                }, 500);
            } catch (e) {
                console.log(e);
                sessionStorage.removeItem("deepLinkInProgress");
                openWarningModal(t_invalidArticleId);
            }
        }
    }

    return {
        ...context,
        loadBookFromRecentBooks,
        loadUserRecentBooks,
        loadBooksInfoFromUUIDsIntoState,
        loadBandInfosForActivitiesIntoState,
        checkForInitArticleId,
        blockedBookSubjects,
        isBookBlocked,
        bookFilters,
    };
}

function BookContextProvider(props: any) {
    const [recentlyUsedBooks, setRecentlyUsedBooks] = useState<RecentBooks>({});
    const [recentBookSubjects, setRecentBookSubjects] = useState<RecentBookSubjects>({});
    const [areRecentBooksLoading, setAreRecentBooksLoading] = useState(false);

    // Exclusive vars for the vokabeltrainer exercises (recurring tasks)
    const [userUsedBooksInfo, setUserUsedBooksInfo] = useState<AvailableBandsInfo>({});
    const [lastSourceSelected, setLastSourceSelected] = useState<SourceType | undefined>(SourceType.BOOK);

    const value = {
        recentlyUsedBooks,
        setRecentlyUsedBooks,
        recentBookSubjects,
        setRecentBookSubjects,
        areRecentBooksLoading,
        setAreRecentBooksLoading,
        userUsedBooksInfo,
        setUserUsedBooksInfo,
        lastSourceSelected,
        setLastSourceSelected,
    };

    return (
        <BookContext.Provider
            value={value}
            {...props}
        />
    );
}

export { BookContextProvider, useBookContext };
